(() => {
  const appId = 'dosha-root';

  const state = {
    step: 0,
    email: '',
    prakritiScores: { vata: 0, pitta: 0, kapha: 0 },
    vikritiConcerns: { primary: '', secondary: '', tertiary: '' },
    rankSelections: {}, // per question: { optionIndex: { rank, dosha } }
    answersApplied: new Set(), // question indexes already scored
  };

  const totalPrakritiQuestions = 4;

  const ICONS = {
    body: svgDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 260">
        <g fill="none" stroke="currentColor" stroke-width="3">
          <circle cx="130" cy="42" r="12" />
          <path d="M130 54v26" />
          <path d="M117 71c-13 18-18 44-18 65 0 28-7 49-13 69" />
          <path d="M143 71c13 18 18 44 18 65 0 28 7 49 13 69" />
          <path d="M92 104c-16 0-28 13-28 29v49" />
          <path d="M168 104c16 0 28 13 28 29v49" />
          <path d="M108 72c0 26 0 55-6 84" />
          <path d="M152 72c0 26 0 55 6 84" />
          <path d="M95 152c6 18 11 39 11 63" />
          <path d="M165 152c-6 18-11 39-11 63" />
        </g>
      </svg>
    `),
    wrist: svgDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 260">
        <g fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M58 194c0-33 18-54 34-70 8-8 13-17 13-28V67c0-9 7-16 16-16s16 7 16 16v65" />
          <path d="M117 132V52c0-9 7-16 16-16s16 7 16 16v78" />
          <path d="M149 132V62c0-9 7-16 16-16s16 7 16 16v69" />
          <path d="M181 136V76c0-8 6-14 14-14s14 6 14 14v69c0 20-9 38-23 52-16 16-33 36-33 62" />
          <path d="M74 198h112" />
          <path d="M61 194c6 9 17 17 31 17h87c17 0 31-14 31-31" />
        </g>
      </svg>
    `),
    vata: svgDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320">
        <circle cx="160" cy="160" r="150" fill="#d6b34d"/>
        <g fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M160 78c-18 24 2 36 19 45 15 8 22 24 16 42-7 19-26 29-45 25-19-4-31-20-31-39 0-24 22-30 33-46 8-11 7-20 8-27z" />
          <path d="M126 145c-22 10-34 31-29 52 7 28 34 45 64 39" />
          <path d="M198 148c20 11 31 30 27 49-5 25-26 45-58 47" />
          <path d="M140 108c-7 11-5 24 6 34" />
        </g>
      </svg>
    `),
    pitta: svgDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320">
        <circle cx="160" cy="160" r="150" fill="#b75a33"/>
        <g fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M160 74c-19 25-39 47-39 80 0 28 18 51 39 67 21-16 39-39 39-67 0-33-20-55-39-80z" />
          <path d="M161 117c-8 18 8 22 16 35 8 12 7 31-4 42" />
          <path d="M138 141c-10 10-12 24-6 38" />
        </g>
      </svg>
    `),
    kapha: svgDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320">
        <circle cx="160" cy="160" r="150" fill="#346f86"/>
        <g fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M208 103c-27 0-50 18-62 41-10 19-8 38-1 56 8 20 26 36 50 36 31 0 58-25 58-56 0-35-20-77-45-77z" />
          <path d="M158 163c17-19 49-25 64-8" />
          <path d="M180 128c12 2 21 8 28 18" />
        </g>
      </svg>
    `),
    hero: "https://cdn.shopify.com/s/files/1/0453/3642/8710/files/wooden-spoons-with-plants-view.jpg_Medium_fea1de84-818f-420e-92d1-cce32623b496.jpg?v=1775568087",
  };

  const PRAKRITI_QUESTIONS = [
    {
      title: 'What is your natural body frame & weight tendency?',
      subtitle: 'Rank from 1 to 3, with 1 being the most true.',
      type: 'figure',
      options: [
        {
          text: 'Thin / Light',
          dosha: 'vata',
          img: 'https://cdn.shopify.com/s/files/1/0453/3642/8710/files/vata_body.webp?v=1775568474',
          info: [
            'Thin, light frame',
            'Hard to gain weight',
            'Prominent joints/veins',
            'Irregular appetite'
          ]
        },
        {
          text: 'Medium / Muscular',
          dosha: 'pitta',
          img: 'https://cdn.shopify.com/s/files/1/0453/3642/8710/files/pitta_body.webp?v=1775477584',
          info: [
            'Medium, athletic build',
            'Moderate weight change',
            'Well-proportioned frame',
            'Strong appetite'
          ]
        },
        {
          text: 'Broad / Sturdy',
          dosha: 'kapha',
          img: 'https://cdn.shopify.com/s/files/1/0453/3642/8710/files/kapha_body.webp?v=1775568474',
          info: [
            'Large, sturdy build',
            'Tendency to gain weight',
            'Broad shoulders & hips',
            'Consistent appetite'
          ]
        }
      ]
    },

    {
      title: 'What is your natural skin type & texture?',
      subtitle: 'Rank from 1 to 3, with 1 being the most true.',
      type: 'figure',
      options: [
        {
          text: 'Dry / Rough',
          dosha: 'vata',
          img: 'https://cdn.shopify.com/s/files/1/0453/3642/8710/files/image_39.png?v=1775568769',
          info: [
            'Dry, thin, rough skin',
            'Prone to cracking/flaking',
            'Cool to the touch',
            'Darkish complexion'
          ]
        },
        {
          text: 'Oily / Sensitive',
          dosha: 'pitta',
          img: 'https://cdn.shopify.com/s/files/1/0453/3642/8710/files/image_38.png?v=1775568917',
          info: [
            'Oily, sensitive, warm',
            'Prone to rashes/acne',
            'Reddish or fair tone',
            'Freckles/moles common'
          ]
        },
        {
          text: 'Thick / Moist',
          dosha: 'kapha',
          img: 'https://cdn.shopify.com/s/files/1/0453/3642/8710/files/Frame_2.png?v=1775568769',
          info: [
            'Thick, smooth, moist',
            'Oily but rarely irritated',
            'Cool & pale complexion',
            'Ages slowly'
          ]
        }
      ]
    },

    {
      title: 'How do you think, learn & react emotionally?',
      subtitle: 'Rank from 1 to 3, with 1 being the most true.',
      type: 'text',
      options: [
        {
          text: 'Quick / Anxious',
          dosha: 'vata',
          info: [
            'Quick mind, learns fast',
            'Forgets easily',
            'Anxious or worried',
            'Creative & changeable',
            'Light, interrupted sleep'
          ]
        },
        {
          text: 'Sharp / Intense',
          dosha: 'pitta',
          info: [
            'Sharp intellect',
            'Goal-oriented & driven',
            'Irritable under stress',
            'Competitive & decisive',
            'Moderate sleep with vivid dreams'
          ]
        },
        {
          text: 'Calm / Steady',
          dosha: 'kapha',
          info: [
            'Slow to learn but retains well',
            'Calm & patient nature',
            'Emotionally stable & loyal',
            'Deep, long sleep',
            'Resistant to change'
          ]
        }
      ]
    },

    {
      title: 'How is your digestion, energy & daily rhythm?',
      subtitle: 'Rank from 1 to 3, with 1 being the most true.',
      type: 'text',
      options: [
        {
          text: 'Variable / Erratic',
          dosha: 'vata',
          info: [
            'Irregular digestion',
            'Bloating or gas common',
            'Energy comes in bursts',
            'Cold hands & feet',
            'Prefers warmth'
          ]
        },
        {
          text: 'Strong / Intense',
          dosha: 'pitta',
          info: [
            'Strong digestion',
            'Gets irritable if hungry',
            'Consistent energy levels',
            'Runs warm, dislikes heat',
            'Tendency to acidity/heartburn'
          ]
        },
        {
          text: 'Slow / Steady',
          dosha: 'kapha',
          info: [
            'Slow but steady digestion',
            'Can skip meals easily',
            'Enduring stamina',
            'Cool & moist body',
            'Dislikes cold & damp'
          ]
        }
      ]
    }
  ];

  const CONCERN_OPTIONS = [
    'Arthritis',
    'Asthma',
    'Cholesterol',
    'Constipation',
    'Diabetes',
    'Digestion',
    'General Wellness',
    'Hair',
    'Hormonal Balance',
    'Immunity',
    'Migraine',
    'Sinus',
    'Skin',
    'Stress & Sleep',
    'Thyroid',
    'Varicose Veins',
    'Weight'
  ];

  const RESULT_DETAILS = {
    vata: {
      leftTitle: "Vata Imbalance Type",
      rightTitle: "Vata",

      intro:
        "Your responses indicate that your system is currently experiencing a Vata imbalance, which in Ayurveda is linked to movement, dryness, and nervous system activity.",

      symptomsTitle: "When Vata goes out of balance, it can show up as:",
      symptoms: [
        "Anxiety, overthinking, restlessness",
        "Poor or disturbed sleep",
        "Dryness (skin, hair, digestion)",
        "Bloating, irregular digestion",
        "Fatigue with mental overactivity"
      ],

      needsTitle: "What Your Body Needs Right Now",
      bodyNeeds: [
        "Calming the nervous system",
        "Improving digestion gently",
        "Reducing internal dryness",
        "Creating stability and routine"
      ],

      productsTitle: "Recommended Products for You",
      comboTitle: "Vata Balance & Nervous System Support Combo",
      products: [
        {
          name: "Pudina Saunf Tulasi Capsules",
          link: "https://www.bilvamherbals.com/products/pudina-saunf-tulasi-capsules-120n",
          subtitle: "Core support",
          benefits: [
            "Helps calm the mind",
            "Supports sleep and reduces restlessness",
            "Improves digestion under stress"
          ],
          note: "Ideal for overthinking + poor sleep patterns"
        },
        {
          name: "Bael Leaf Capsules",
          link: "https://www.bilvamherbals.com/products/bael-leaf-capsules-120n",
          subtitle: "Gut + grounding support",
          benefits: [
            "Supports digestion and reduces bloating",
            "Helps stabilise metabolism",
            "Reduces gut-related Vata imbalance"
          ],
          note: "Important because Vata imbalance often starts in the gut"
        },
        {
          name: "Arrowroot Powder",
          link: "https://www.bilvamherbals.com/products/arrowroot-powder-digestion-inflammation",
          subtitle: "Cooling + nourishment",
          benefits: [
            "Helps reduce internal heat and weakness",
            "Supports energy without overstimulation",
            "Gentle on digestion"
          ]
        },
        {
          name: "Bael Infused Sesame Oil",
          link: "https://www.bilvamherbals.com/products/bael-infused-sesame-oil-126ml",
          subtitle: "Daily calming ritual",
          benefits: [
            "Supports relaxation and nervous system balance",
            "Helps improve sleep quality",
            "Reduces body dryness"
          ],
          note: "Use as a night foot massage ritual"
        }
      ],

      howToUseTitle: "How to Use",
      howToUse: {
        morning: [
          "Bael Leaf Capsules - 2 capsules with warm water",
          "Arrowroot drink - along with mishri powder"
        ],
        afternoon: [
          "Pudina Saunf Tulasi Capsules - 2 capsules per time with plain water after lunch and dinner"
        ],
        night: [
          "Bael Oil (foot massage before sleep)"
        ]
      },

      expectedTitle: "With consistent use and routine:",
      expected: [
        "Better sleep quality",
        "Reduced anxiety and restlessness",
        "Improved digestion",
        "More stable energy through the day"
      ],

      noteTitle: "Note",
      note:
        "This recommendation is based on your responses and is a general guideline. Your body type, lifestyle, your place of stay, season when you have sought consultation and health history may require personalised adjustments.",

      optionalTitle: "Optional",
      optionalText:
        "Does your stress show up as hair fall or dryness? You may also benefit from Herbal Hair Oil (for scalp relaxation and nourishment)."
    },

    pitta: {
      leftTitle: "Pitta Calm Imbalance",
      rightTitle: "Pitta",

      intro:
        "Your responses indicate a Pitta imbalance. This often shows up as internal heat, acidity, irritability, and mental intensity.",

      symptomsTitle: "When Pitta goes out of balance, it can show up as:",
      symptoms: [
        "Internal heat",
        "Irritability and mental intensity",
        "Acidity or burning sensation",
        "Heat-related skin issues",
        "Stress-related scalp and head tension"
      ],

      needsTitle: "What Your Body Needs Right Now",
      bodyNeeds: [
        "Cooling the system",
        "Soothing digestion",
        "Reducing internal heat",
        "Supporting emotional calm"
      ],

      productsTitle: "Recommended Products for You",
      comboTitle: "Pitta Calm Support Combo",
      products: [
        {
          name: "Pudina Saunf Tulasi Capsules",
          link: "https://www.bilvamherbals.com/products/pudina-saunf-tulasi-capsules-120n",
          subtitle: "Core internal cooling",
          benefits: [
            "Helps reduce internal heat",
            "Calms irritability and mental intensity",
            "Supports digestion without aggravating acidity"
          ],
          note: "Ideal for heat + stress patterns"
        },
        {
          name: "Bael Leaf Capsules",
          link: "https://www.bilvamherbals.com/products/bael-leaf-capsules-120n",
          subtitle: "Gut + inflammation support",
          benefits: [
            "Soothes digestion",
            "Helps reduce acidity and gut irritation",
            "Supports metabolic balance"
          ]
        },
        {
          name: "Arrowroot Powder",
          link: "https://www.bilvamherbals.com/products/arrowroot-powder-digestion-inflammation",
          subtitle: "Cooling nourishment",
          benefits: [
            "Naturally cools the system",
            "Helps reduce burning sensation and weakness",
            "Gentle, easy-to-digest energy support"
          ]
        },
        {
          name: "Bael Infused Sesame Oil",
          link: "https://www.bilvamherbals.com/products/bael-infused-sesame-oil-126ml",
          subtitle: "Night relaxation ritual",
          benefits: [
            "Calms the nervous system",
            "Helps improve sleep",
            "Reduces internal heat through external application"
          ],
          note: "Use as foot massage before sleep"
        },
        {
          name: "Herbal Hair Oil",
          link: "https://www.bilvamherbals.com/products/herbal-hair-oil-126ml",
          subtitle: "Scalp cooling + stress release",
          benefits: [
            "Helps reduce scalp heat",
            "Supports hair health during stress",
            "Relieves head tension"
          ],
          note: "Use as a 3 times a week scalp massage ritual"
        },
        {
          name: "Chandan Bath Powder",
          link: "https://www.bilvamherbals.com/products/chandan-herbal-bathing-powder-face-body-pack-skin-tan",
          subtitle: "Daily cooling skin support",
          benefits: [
            "Naturally cools and soothes the skin",
            "Helps with heat-related skin issues",
            "Refreshes the body and reduces irritation"
          ],
          note: "Use as a daily bath ritual for full-body cooling"
        }
      ],

      howToUseTitle: "How to Use (simple routine)",
      howToUse: {
        morning: [
          "Bael Leaf Capsules - 2 capsules with warm water after breakfast.",
          "Arrowroot drink - with mishri powder 1 teaspoon powder and water after washing arrowroot powder thoroughly and soaking overnight in 50ml water.",
          "Chandan Bath Powder (during bath)"
        ],
        afternoon: [
          "Pudina Saunf Tulasi Capsules - 2 capsules per time with plain water after lunch and dinner"
        ],
        night: [
          "Bael Oil (foot and calf muscle massage)",
          "Herbal Hair Oil (scalp massage) - daily or minimum 3 times a week."
        ]
      },

      expectedTitle: "What You Can Expect",
      expected: [
        "Reduced acidity and internal heat",
        "Calmer mood and better emotional balance",
        "Improved skin comfort and scalp health",
        "Better sleep and relaxation"
      ],

      noteTitle: "Important",
      note:
        "This recommendation is based on your responses and is a general guideline. For deeper and long-term results, a personalised approach is always more effective."
    },

    kapha: {
      leftTitle: "Kapha Aggravation Imbalance",
      rightTitle: "Kapha",

      intro:
        "Your responses indicate a Kapha imbalance, which is associated with heaviness, mucus, and sluggish metabolism.",

      symptomsTitle: "When Kapha increases, it can show up as:",
      symptoms: [
        "Mucus, congestion, sinus issues",
        "Frequent colds or heaviness in chest",
        "Sluggish digestion",
        "Weight gain or water retention",
        "Low energy, lethargy",
        "Feeling stuck physically and mentally"
      ],

      needsTitle: "What Your Body Needs Right Now",
      bodyNeeds: [
        "Clearing excess mucus and congestion",
        "Activating metabolism and digestion",
        "Reducing heaviness and accumulation",
        "Stimulating energy and movement"
      ],

      productsTitle: "Recommended Products for You",
      comboTitle: "Kapha Clearing & Activation System",
      products: [
        {
          name: "Vasa Leaf Powder",
          link: "https://www.bilvamherbals.com/products/vasa-powde",
          subtitle: "Respiratory clearing support",
          benefits: [
            "Helps clear mucus and congestion",
            "Supports lung function and breathing",
            "Useful in cough, cold, and sinus heaviness"
          ],
          note: "Core support when Kapha affects the respiratory system"
        },
        {
          name: "Tulasi Turmeric Blend",
          link: "https://www.bilvamherbals.com/products/tulasi-turmeric-blend",
          subtitle: "Immunity + anti-congestion",
          benefits: [
            "Supports respiratory health",
            "Helps reduce inflammation and buildup",
            "Strengthens natural defense"
          ],
          note: "Works well alongside Vasa for clearing + protection"
        },
        {
          name: "Wild Forest Honey",
          link: "https://www.bilvamherbals.com/products/raw-honey-forest-immunity-digestion-salad-cold-beverages",
          subtitle: "Kapha balancing support",
          benefits: [
            "Helps reduce mucus accumulation",
            "Supports throat and respiratory comfort",
            "Traditionally used to enhance absorption of herbs"
          ],
          note: "Best taken with Vasa / Tulasi formulations"
        },
        {
          name: "Edible Camphor Infused Sesame Oil",
          link: "https://www.bilvamherbals.com/products/edible-camphor-infused-sesame-oil-30ml",
          subtitle: "Nasal Drops",
          benefits: [
            "Helps open up nasal passages",
            "Supports relief from heaviness in head",
            "Provides a sense of clarity and lightness"
          ]
        },
        {
          name: "Bhuiamlaki + Dry Ginger + Harshringar",
          link: "https://www.bilvamherbals.com/products/bhuiamlaki-dry-ginger-harshshringar-capsules-120n",
          subtitle: "Add if weight gain is present",
          benefits: [
            "Supports fat metabolism",
            "Improves digestion (Agni)",
            "Helps reduce accumulation"
          ],
          note: "Recommended if you are experiencing weight gain, belly fat, or slow metabolism"
        },
        {
          name: "Neem Bath Powder",
          link: "https://www.bilvamherbals.com/products/herbal-bathing-powder-neem",
          subtitle: "Daily cleansing ritual",
          benefits: [
            "Helps reduce oiliness and stickiness",
            "Supports skin cleansing",
            "Promotes freshness and lightness"
          ],
          note: "Use as a daily bath routine to reduce Kapha buildup"
        }
      ],

      howToUseTitle: "How to Use",
      howToUse: {
        morning: [
          "Vasa Leaf Powder - Take as decoction after breakfast, Lunch and dinner",
          "Tulasi Turmeric Blend - with honey (1/2 teaspoon blend with honey for 15 days) and also with drinking water (1 pinch in 1 litre water)"
        ],
        afternoon: [
          "Vasa decoction after food"
        ],
        evening: [
          "Vasa Leaf Powder decoction (if needed)"
        ],
        ifWeightGain: [
          "Add Bhuiamlaki blend - 2 capsules per time with warm water after lunch and dinner"
        ],
        daily: [
          "Neem Bath Powder"
        ],
        external: [
          "Edible Camphor Infused Sesame oil - 2 drops in each nostril by lifting head up, take deep breaths 8-10 - first thing in the morning and night before sleeping. Aggravated conditions - take 2 times additionally during the day."
        ]
      },

      expectedTitle: "What You Can Expect",
      expected: [
        "Reduced mucus and congestion",
        "Improved breathing and clarity",
        "Better digestion and lighter feeling",
        "Increased energy and movement",
        "Gradual support in weight balance"
      ],

      noteTitle: "Important",
      note:
        "This recommendation is a general guideline based on your responses. Kapha imbalance improves faster when supported with regular movement, light, warm diet, and consistent routine."
    },

    "vata-pitta": {
      leftTitle: "Vata & Pitta Aggravation Imbalance",
      rightTitle: "Vata & Pitta",

      intro:
        "Your responses indicate a combined Vata–Pitta imbalance. This means your system is experiencing both overactivity (Vata) and heat/intensity (Pitta).",

      symptomsTitle: "How This Typically Shows Up",
      symptoms: [
        "Anxiety + irritability",
        "Overthinking + impatience",
        "Poor or disturbed sleep",
        "Acidity, burning sensation",
        "Irregular digestion (sometimes bloating, sometimes acidity)",
        "Feeling wired but exhausted"
      ],

      needsTitle: "What Your Body Needs Right Now",
      bodyNeeds: [
        "Calming the nervous system (Vata)",
        "Cooling internal heat (Pitta)",
        "Stabilising digestion gently",
        "Reducing both dryness and inflammation"
      ],

      productsTitle: "Recommended Products for You",
      products: [
        {
          name: "Pudina Saunf Tulasi Capsules",
          link: "https://www.bilvamherbals.com/products/pudina-saunf-tulasi-capsules-120n",
          subtitle: "Core support",
          benefits: [
            "Calms the mind and reduces restlessness",
            "Helps improve sleep",
            "Supports digestion without increasing heat"
          ],
          note: "Ideal for stress + acidity + poor sleep"
        },
        {
          name: "Bael Leaf Capsules",
          link: "https://www.bilvamherbals.com/products/bael-leaf-capsules-120n",
          subtitle: "Gut stabilising support",
          benefits: [
            "Helps balance digestion",
            "Reduces both bloating and acidity",
            "Supports gut healing"
          ],
          note: "Important for mixed digestion patterns"
        },
        {
          name: "Arrowroot Powder",
          link: "https://www.bilvamherbals.com/products/arrowroot-powder-digestion-inflammation",
          subtitle: "Cooling + nourishment",
          benefits: [
            "Reduces internal heat",
            "Supports gentle energy without overstimulation",
            "Easy on digestion"
          ],
          note: "Useful when feeling burnt out + weak"
        },
        {
          name: "Bael Infused Sesame Oil",
          link: "https://www.bilvamherbals.com/products/bael-infused-sesame-oil-126ml",
          subtitle: "Nervous system reset",
          benefits: [
            "Calms the body and mind",
            "Supports deeper sleep",
            "Reduces dryness and internal heat"
          ],
          note: "Use as a night foot massage ritual"
        },
        {
          name: "Herbal Hair Oil",
          link: "https://www.bilvamherbals.com/products/herbal-hair-oil-126ml",
          subtitle: "Head cooling + relaxation",
          benefits: [
            "Helps reduce scalp heat",
            "Relieves tension",
            "Supports better sleep indirectly"
          ],
          note: "Use as a scalp massage"
        },
        {
          name: "Chandan Bath Powder",
          link: "https://www.bilvamherbals.com/products/chandan-herbal-bathing-powder-face-body-pack-skin-tan",
          subtitle: "Daily cooling support",
          benefits: [
            "Helps reduce body heat",
            "Soothes skin and irritation",
            "Promotes freshness and calmness"
          ],
          note: "Use as a daily bath ritual"
        }
      ],

      howToUseTitle: "How to Use (simple routine)",
      howToUse: {
        morning: [
          "Bael Leaf Capsules - 2 capsules with warm water after breakfast",
          "Arrowroot drink - with mishri after thoroughly wash 1 teaspoon powder in water 2-3 times the previous night and soaking in 50ml water.",
          "Chandan Bath Powder (during bath)"
        ],
        afternoon: [
          "Pudina Saunf Tulasi Capsules - 2 capsules with plain water"
        ],
        night: [
          "Pudina Saunf Tulasi Capsules - 2 capsules with plain water",
          "Bael Oil (foot and calf muscles massage)",
          "Herbal Hair Oil (scalp massage Weekly (2–3 times))"
        ]
      },

      expectedTitle: "What You Can Expect",
      expected: [
        "Reduced anxiety and irritability",
        "Better sleep quality",
        "Reduced acidity and internal heat",
        "More stable digestion",
        "Improved overall calmness and energy"
      ],

      noteTitle: "Important",
      note:
        "This recommendation is a general starting point based on your responses. Vata–Pitta imbalance is sensitive and often requires dietary adjustments, routine stabilisation, and personalised fine-tuning.",

      whyThisWorksTitle: "Why this works",
      whyThisWorks: [
        "Calms without making you dull",
        "Cools without weakening digestion",
        "Supports both mind and body together"
      ]
    },

    "pitta-kapha": {
      leftTitle: "Pitta–Kapha Imbalance",
      rightTitle: "Pitta–Kapha",

      intro:
        "This combination often shows up as acidity + heaviness, weight gain with inflammation, skin issues + oiliness, and irritability + sluggishness.",

      symptomsTitle: "How This Shows Up",
      symptoms: [
        "Acidity + heaviness",
        "Weight gain with inflammation",
        "Skin issues + oiliness",
        "Irritability + sluggishness"
      ],

      needsTitle: "What Your Body Needs",
      bodyNeeds: [
        "Cooling without adding heaviness",
        "Lightening digestion without overheating",
        "Reducing oiliness, inflammation, and buildup"
      ],

      productsTitle: "Recommended Products for You",
      products: [
        {
          name: "Pudina Saunf Tulasi Capsules",
          link: "https://www.bilvamherbals.com/products/pudina-saunf-tulasi-capsules-120n",
          subtitle: "Core",
          benefits: [
            "Cools digestion",
            "Reduces acidity and irritability",
            "Helps with post-meal heaviness"
          ],
          note: "Balances both Pitta (heat) and Kapha (sluggish digestion)"
        },
        {
          name: "Bael Leaf Capsules",
          link: "https://www.bilvamherbals.com/products/bael-leaf-capsules-120n",
          subtitle: "Gut + metabolic balance",
          benefits: [
            "Supports digestion gently",
            "Helps reduce bloating and heaviness",
            "Does not aggravate heat"
          ]
        },
        {
          name: "Neem Bath Powder",
          link: "https://www.bilvamherbals.com/products/herbal-bathing-powder-neem",
          subtitle: "Daily cleansing support",
          benefits: [
            "Reduces oiliness and stickiness",
            "Helps with heat-related skin issues",
            "Promotes lightness and freshness"
          ]
        },
        {
          name: "Herbal Hair Oil",
          link: "https://www.bilvamherbals.com/products/herbal-hair-oil-126ml",
          subtitle: "Light use",
          benefits: [
            "Helps reduce scalp heat",
            "Supports oil balance",
            "Relieves tension"
          ],
          note: "Use lightly (avoid heavy oiling)"
        },
        {
          name: "Arrowroot Powder",
          link: "https://www.bilvamherbals.com/products/arrowroot-powder-digestion-inflammation",
          subtitle: "Conditional",
          benefits: [
            "Helps cool the system",
            "Provides gentle nourishment without aggravation"
          ],
          note: "Use only if you experience acidity, burning sensation, or weakness"
        }
      ],

      avoidTitle: "What We Avoid Here",
      avoid: [
        "Heavy formulations",
        "Strong heating herbs",
        "Over-stimulating metabolism boosters"
      ],

      expectedTitle: "What You Can Expect",
      expected: [
        "Reduced acidity and internal heat",
        "Lighter digestion and less heaviness",
        "Better skin comfort and reduced oiliness",
        "Improved energy without irritation"
      ],

      noteTitle: "Important",
      note:
        "This is a balanced approach to avoid aggravating either dosha. For stronger weight concerns, metabolic correction should be introduced carefully and individually, not as a default.",

      whyThisWorksTitle: "Why this works",
      whyThisWorks: [
        "Prevents worsening of Pitta while addressing Kapha",
        "Keeps cooling support without creating heaviness",
        "Helps avoid over-stimulating metabolism"
      ]
    },

    "vata-kapha": {
      leftTitle: "Vata & Kapha Aggravation Imbalance",
      rightTitle: "Vata & Kapha",

      intro:
        "Your responses indicate a combined Vata–Kapha imbalance. This means your system is experiencing both sluggishness and accumulation (Kapha) and irregularity and nervous system overactivity (Vata).",

      symptomsTitle: "How This Typically Shows Up",
      symptoms: [
        "Sluggish but anxious",
        "Bloating with heaviness",
        "Low energy but restless mind",
        "Congestion, mucus, or sinus issues",
        "Irregular digestion (sometimes slow, sometimes disturbed)"
      ],

      needsTitle: "What Your Body Needs Right Now",
      bodyNeeds: [
        "Activation without overstimulation",
        "Clearing mucus and heaviness",
        "Stabilising digestion gently",
        "Calming the nervous system without adding heaviness"
      ],

      productsTitle: "Recommended Products for You",
      products: [
        {
          name: "Pudina Saunf Tulasi Capsules",
          link: "https://www.bilvamherbals.com/products/pudina-saunf-tulasi-capsules-120n",
          subtitle: "Core support",
          benefits: [
            "Balances digestion",
            "Provides mild stimulation without overheating",
            "Helps reduce both heaviness and irregularity"
          ],
          note: "Ideal for mixed digestion patterns"
        },
        {
          name: "Bael Leaf Capsules",
          link: "https://www.bilvamherbals.com/products/bael-leaf-capsules-120n",
          subtitle: "Gut stabilisation",
          benefits: [
            "Supports digestion and reduces bloating",
            "Helps regulate irregular gut patterns",
            "Prevents excess dryness while improving metabolism"
          ]
        },
        {
          name: "Vasa Leaf Powder",
          link: "https://www.bilvamherbals.com/products/vasa-powde",
          subtitle: "Respiratory clearing support",
          benefits: [
            "Helps reduce mucus and congestion",
            "Supports lung function and breathing",
            "Useful in heaviness in chest or sinus"
          ]
        },
        {
          name: "Tulasi Turmeric Blend",
          link: "https://www.bilvamherbals.com/products/tulasi-turmeric-blend",
          subtitle: "Anti-congestion + support",
          benefits: [
            "Helps reduce buildup and inflammation",
            "Supports respiratory and immune balance"
          ]
        },
        {
          name: "Wild Forest Honey",
          link: "https://www.bilvamherbals.com/products/raw-honey-forest-immunity-digestion-salad-cold-beverages",
          subtitle: "Kapha balancing support",
          benefits: [
            "Helps reduce mucus accumulation",
            "Enhances absorption of herbs",
            "Supports throat and respiratory comfort"
          ]
        },
        {
          name: "Bael Infused Sesame Oil",
          link: "https://www.bilvamherbals.com/products/bael-infused-sesame-oil-126ml",
          subtitle: "Night grounding ritual",
          benefits: [
            "Calms the nervous system",
            "Supports better sleep",
            "Helps balance Vata without increasing heaviness"
          ],
          note: "Use as a foot massage before sleep"
        },
        {
          name: "Neem Bath Powder",
          link: "https://www.bilvamherbals.com/products/herbal-bathing-powder-neem",
          subtitle: "Daily cleansing support",
          benefits: [
            "Helps reduce stickiness and oiliness",
            "Promotes lightness and freshness",
            "Supports Kapha reduction externally"
          ]
        }
      ],

      howToUseTitle: "How to Use",
      howToUse: {
        morning: [
          "Tulasi Turmeric Blend - 1/2 teaspoon with Honey and have it first thing in the morning for 15 days. Also mix 1 pinch of powder in 1 litre drinking water, drink this water only; in a day if 3-4 litres then drink that entire water by making it like this."
        ],
        afternoon: [
          "Pudina Saunf Tulasi Capsules: 2 capsules with plain water after lunch"
        ],
        night: [
          "Pudina Saunf Tulasi Capsules: 2 capsules with plain water after dinner",
          "Bael Oil (foot massage)"
        ],
        daily: [
          "Neem Bath Powder"
        ]
      },

      expectedTitle: "What You Can Expect",
      expected: [
        "Reduced congestion and heaviness",
        "Improved digestion and gut regularity",
        "Better energy with less restlessness",
        "Calmer mind with lighter body"
      ],

      noteTitle: "Important",
      note:
        "This is a balanced approach to avoid aggravating either Vata or Kapha. Avoid excess fasting and heavy, cold foods.",

      whyThisWorksTitle: "Why this works",
      whyThisWorks: [
        "Supports activation without overstimulation",
        "Clears mucus and heaviness while calming the mind",
        "Helps keep digestion stable"
      ]
    },

    tridoshic: {
      leftTitle: "Vata–Pitta–Kapha Imbalance",
      rightTitle: "Vata–Pitta–Kapha",

      intro:
        "Your responses indicate that all three doshas are currently out of balance. This usually reflects a deeper level of imbalance where the body is experiencing irregularity and restlessness, internal heat and irritation, and heaviness and sluggishness.",

      symptomsTitle: "How This Typically Feels",
      symptoms: [
        "Digestion that keeps fluctuating (bloating, acidity, heaviness)",
        "Low energy but an overactive mind",
        "Disturbed or inconsistent sleep",
        "Feeling physically and mentally out of sync",
        "Symptoms that do not follow a clear pattern"
      ],

      needsTitle: "What This Means",
      bodyNeeds: [
        "The imbalance is not isolated to one system",
        "The body requires a carefully sequenced approach",
        "Generic or self-guided solutions may not give consistent results"
      ],

      productsTitle: "",
      products: [],

      howToUseTitle: "",
      howToUse: {},

      expectedTitle: "",
      expected: [],

      noteTitle: "",
      note:
        "When all three doshas are aggravated, it is a sign that the imbalance is not isolated to one system and the body requires a carefully sequenced approach. Generic or self-guided solutions may not give consistent results",

      selfStartTitle:
        "If you would like to start on your own, you may begin with a simple, stabilising routine focused on:",
      selfStart: [
        "regular meals",
        "proper sleep timing",
        "light, easy-to-digest foods"
      ]
    }
  };

  function svgDataUri(svg) {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
  }

  function $(selector, root = document) {
    return root.querySelector(selector);
  }

  function $all(selector, root = document) {
    return Array.from(root.querySelectorAll(selector));
  }

  function getRankOrder(questionIndex) {
    const row = state.rankSelections[questionIndex] || {};
    const values = Object.values(row);
    const ranks = values.map((x) => x.rank);
    return ranks;
  }

  function isQuestionComplete(questionIndex) {
    const row = state.rankSelections[questionIndex];
    if (!row) return false;

    const ranks = Object.values(row).map(x => x.rank);
    return ranks.length === 3 && [1, 2, 3].every(r => ranks.includes(r));
  }

  function uniqueRanksValid(row) {
    const ranks = Object.values(row).map((x) => x.rank);
    return new Set(ranks).size === ranks.length && [1, 2, 3].every((n) => ranks.includes(n));
  }


  function calculateScores() {
    const scores = { vata: 0, pitta: 0, kapha: 0 };

    for (let qIndex = 0; qIndex < PRAKRITI_QUESTIONS.length; qIndex++) {
      const row = state.rankSelections[qIndex];
      if (!row) continue;

      Object.values(row).forEach((item) => {
        const weight = 4 - item.rank;
        scores[item.dosha] += weight;
      });
    }

    return scores;
  }

  function getDoshaResult(scores) {
    const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);

    const [first, second, third] = sorted;

    const diff1 = first[1] - second[1];
    const diff2 = second[1] - third[1];

    // ✅ Single Dosha
    if (diff1 >= 3) {
      return first[0];
    }

    // ✅ Tridoshic
    if (diff1 <= 1 && diff2 <= 1) {
      return 'tridoshic';
    }

    // ✅ Dual Dosha
    return `${first[0]}-${second[0]}`;
  }
  function getPercentages(scores) {
    const total = scores.vata + scores.pitta + scores.kapha;

    return {
      vata: Math.round((scores.vata / total) * 100),
      pitta: Math.round((scores.pitta / total) * 100),
      kapha: Math.round((scores.kapha / total) * 100),
    };
  }

  function concernToDosha(concern) {
    const c = (concern || '').toLowerCase();
    if (/(digest|constipat|sinus|asthma|weight)/.test(c)) return 'kapha';
    if (/(skin|hair|migraine|thyroid|choles|acidity|heat|anger|irrit|sweat|prostate|loose)/.test(c)) return 'pitta';
    return 'vata';
  }

  function computeVikritiFromConcerns() {
    const scores = { vata: 0, pitta: 0, kapha: 0 };
    ['primary', 'secondary', 'tertiary'].forEach((key) => {
      const concern = state.vikritiConcerns[key];
      if (!concern) return;
      scores[concernToDosha(concern)] += key === 'primary' ? 3 : key === 'secondary' ? 2 : 1;
    });
    return scores;
  }

  function render() {
    const root = document.getElementById(appId);
    if (!root) return;

    const current = getCurrentStep();

    root.innerHTML = `
      <div class="dq-app dq-step-${current}">
        ${renderStep(current)}
      </div>
    `;

    bindStepEvents(current);
    syncRankHighlights(current);
  }
  function getImbalanceFromScores(scores) {
    const sorted = Object.entries(scores).sort((a, b) => a[1] - b[1]); 

    const [lowest, second, highest] = sorted;

    const diff = second[1] - lowest[1];

    if (diff <= 2) {
      return `${lowest[0]}-${second[0]}`;
    }
    return lowest[0];
  }
  function formatTitle(key) {
    return key
      .split('-')
      .map(k => k.charAt(0).toUpperCase() + k.slice(1))
      .join('-');
  }
  function getCurrentStep() {
    return state.step;
  }

  function renderStep(stepIndex) {
    if (stepIndex === 0) return renderLanding();
    if (stepIndex === 1) return renderIntro('Determining Your Constitution', 'These first questions will determine your Ayurvedic body type. Please answer as truthfully as possible.');
    if (stepIndex >= 2 && stepIndex <= 5) return renderRankQuestion(stepIndex - 2, PRAKRITI_QUESTIONS[stepIndex - 2], stepIndex);
    if (stepIndex === 6) return renderIntro('Determining Your Imbalances', "Now it's time to determine your imbalances. On the next page, we ask you to rank your top three health concerns, which are an effective way to determine energetic imbalances.");
    if (stepIndex === 7) return renderConcerns();
    if (stepIndex === 8) return renderResult();
    return '';
  }

  function renderLanding() {
    return `
      <div class="dq-shell dq-landing">
        <div class="dq-corners"></div>
        <div class="dq-hero">
          <div class="dq-hero-copy">
           

            <div class="dq-kicker">LEARN YOUR AYURVEDIC BODY TYPE</div>
            <h1>More Than A<br>Dosha Quiz</h1>
            <p>Your dosha holds the key to vitality and balance. Find your <strong>dosha</strong> in 3 minutes and get a free personalized 6-part course!</p>

            <form class="dq-email-form" id="dq-email-form">
            <input type="text" id="dq-name" name="name" placeholder="Full Name" required>

  <input type="email" id="dq-email-input" name="email" placeholder="Enter your email" autocomplete="email" required>
  <div class="form-row">
<input type="tel" id="dq-phone" name="phone" pattern="[0-9]{10}" placeholder="Phone no." required>
  <input type="text" id="dq-location" name="city" placeholder="City" required>
  </div>
<div class="form-row">
<div class="form-label-field">
  <select id="dq-age" name="age_group" required>
    <option value="">Select Age Group</option>
    <option value="18-30">18–30</option>
    <option value="30-45">30–45</option>
    <option value="45-60">45–60</option>
    <option value="60+">60+</option>
  </select>
</div>
<div class="form-label-field">
  <select id="dq-gender" name="gender" required>
    <option value="">Select Gender</option>
    <option value="male">Male</option>
    <option value="female">Female</option>
    <option value="prefer_not">Prefer not to say</option>
  </select>
</div>

</div>


  <button type="submit" class="dq-btn dq-btn-red">START NOW!</button>

            </form>

            
          </div>

          <div class="dq-hero-image">
            <img src="${ICONS.hero}" alt="Placeholder hero">
          </div>
        </div>

        <button class="dq-chat-bubble" type="button" aria-label="chat">●●</button>
      </div>
    `;
  }

  function renderIntro(title, text) {
    return `
      <div class="dq-shell dq-intro">
        <div class="dq-corners"></div>
        

        <div class="dq-center-stage dq-intro-stage">
          <h2>${escapeHtml(title)}</h2>
          <p class="dq-intro-copy">${escapeHtml(text)}</p>

          ${title === 'Determining Your Constitution' ? `
            <div class="dq-dosha-triad">
              <div class="dq-dosha-circle dq-vata">
                <img src="${ICONS.vata}" alt="">
                <span class="dq-circle-label bottom">VATA</span>
              </div>
              <div class="dq-dosha-circle dq-pitta">
                <span class="dq-circle-label top">PITTA</span>
                <img src="${ICONS.pitta}" alt="">
              </div>
              <div class="dq-dosha-circle dq-kapha">
                <img src="${ICONS.kapha}" alt="">
                <span class="dq-circle-label bottom">KAPHA</span>
              </div>
            </div>
          ` : ''}
        </div>

        <div class="dq-actions dq-actions-center">
          <button class="dq-btn dq-btn-gold" type="button" data-action="continue">CONTINUE</button>
          <button class="dq-link-btn" type="button" data-action="back">BACK</button>
        </div>
      </div>
    `;
  }

  function renderRankQuestion(questionIndex, q, stepIndex) {
    const isFigure = q.type === 'figure';
    return `
      <div class="dq-shell dq-question-page">
        <div class="dq-corners"></div>

       

        <div class="dq-question-wrap">
          <div class="dq-question-count">QUESTION ${stepIndex - 1} OF 4</div>
          <h2>${escapeHtml(q.title)}</h2>
          <p class="dq-subtitle">${escapeHtml(q.subtitle)}</p>

          <div class="dq-options ${isFigure ? 'dq-options-figure' : 'dq-options-text'}" data-question-index="${questionIndex}">
            ${q.options
        .map(
          (opt, idx) => `
                <div class="dq-option-row" data-option-index="${idx}" data-dosha="${opt.dosha}">
                  ${isFigure ? `<div class="dq-figure"><img src="${opt.img}" alt=""></div>` : ''}
                  
                  <div class="dq-option-header">
  <div class="dq-option-copy">
    ${isFigure
              ? `<div class="info-wrapper"><div class="dq-option-text">${escapeHtml(opt.text).replace(/\n/g, '<br>')}</div> 
  <div class="dq-info-icon" data-info='${JSON.stringify(opt.info)}'><svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none"><circle opacity="0.5" cx="12" cy="12" r="10" stroke="#1C274C" stroke-width="1.5"/>
<path d="M12 17V11" stroke="#1C274C" stroke-width="1.5" stroke-linecap="round"/>
<circle cx="1" cy="1" r="1" transform="matrix(1 0 0 -1 11 9)" fill="#1C274C"/>
</svg>
    </div>  `
              : `<div class="info-wrapper"><div class="dq-option-text dq-option-text-plain">${escapeHtml(opt.text)}</div>
  <div class="dq-info-icon" data-info='${JSON.stringify(opt.info)}'><svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24" fill="none"><circle opacity="0.5" cx="12" cy="12" r="10" stroke="#1C274C" stroke-width="1.5"/>
<path d="M12 17V11" stroke="#1C274C" stroke-width="1.5" stroke-linecap="round"/>
<circle cx="1" cy="1" r="1" transform="matrix(1 0 0 -1 11 9)" fill="#1C274C"/>
</svg>
     </div> `
            }
  </div>

</div>
</div>
                  <div class="dq-rank-group" aria-label="ranking options">
                    <button type="button" class="dq-rank-btn" data-rank="1">1</button>
                    <button type="button" class="dq-rank-btn" data-rank="2">2</button>
                    <button type="button" class="dq-rank-btn" data-rank="3">3</button>
                  </div>
                </div>
              `
        )
        .join('')}
          </div>
        </div>

        <div class="dq-actions dq-actions-center">
          <button class="dq-btn dq-btn-gold" type="button" data-action="continue">CONTINUE</button>
          <button class="dq-link-btn" type="button" data-action="back">BACK</button>
        </div>
      </div>
    `;
  }

  function renderConcerns() {
    return `
      <div class="dq-shell dq-concerns">
        <div class="dq-corners"></div>

        <div class="dq-topbar">
          <div class="dq-question-count">QUESTION 1 OF 1</div>
        </div>

        <div class="dq-question-wrap dq-concern-wrap">
          <h2>What are your top three health concerns?</h2>
          <p class="dq-subtitle">Choose up to three health concerns that are most important to you.</p>

          <div class="dq-select-stack">
            <div class="dq-field">
              <label>Primary Concern (Required):</label>
              <select id="dq-primary" required>
                <option value="" selected disabled>Select one</option>
                ${CONCERN_OPTIONS.map((x) => `<option value="${escapeAttr(x)}">${escapeHtml(x)}</option>`).join('')}
              </select>
            </div>

            <div class="dq-field">
              <label>Secondary Concern:</label>
              <select id="dq-secondary">
                <option value="" selected>Select one</option>
                ${CONCERN_OPTIONS.map((x) => `<option value="${escapeAttr(x)}">${escapeHtml(x)}</option>`).join('')}
              </select>
            </div>

            <div class="dq-field">
              <label>Tertiary Concern:</label>
              <select id="dq-tertiary">
                <option value="" selected>Select one</option>
                ${CONCERN_OPTIONS.map((x) => `<option value="${escapeAttr(x)}">${escapeHtml(x)}</option>`).join('')}
              </select>
            </div>
          </div>
        </div>

        <div class="dq-actions dq-actions-center">
          <button class="dq-btn dq-btn-gold" type="button" data-action="continue">CONTINUE</button>
          <button class="dq-link-btn" type="button" data-action="back">BACK</button>
        </div>
      </div>
    `;
  }

  function renderResult() {
    const prakritiScores = calculateScores();
    const resultType = getDoshaResult(prakritiScores);
    const vikriti = resultType;
    const prakritiKey = normalizeResultKey(resultType);
    const vikritiKey = normalizeResultKey(vikriti);
    const pr = RESULT_DETAILS[prakritiKey] || RESULT_DETAILS.tridoshic;
    const vr = RESULT_DETAILS[vikritiKey] || RESULT_DETAILS.tridoshic;

    return `
    <div class="dq-shell dq-result-page">
      <div class="dq-result-grid">

        <!-- LEFT -->
        <div class="dq-result-main">
          <div class="dq-result-card">

            <div class="dq-result-left-content">
              <div class="dq-result-kicker"> ${escapeHtml(pr.leftTitle || pr.title || 'Result')}</div>

              <div class="dq-result-section">

${prakritiKey === 'tridoshic' ? `
  <p class="dq-result-paragraph">
    ${escapeHtml(pr.note)}
  </p>
` : `
  <h3>
    Recommended Products for You
  </h3>
`}

  ${pr.products ? `
    <div class="dq-products">
      ${pr.products.map(product => `
        <div class="dq-product-card">

          <h4>${product.name}</h4>

          ${product.subtitle ? `
            <div class="dq-product-subtitle">
              ${product.subtitle}
            </div>
          ` : ''}

          <ul>
            ${product.benefits.map(b => `
              <li>${b}</li>
            `).join('')}
          </ul>

          ${product.link ? `
            <a href="${product.link}" target="_blank" class="dq-product-btn">
              View Product
            </a>
          ` : `
            <button class="dq-product-btn">
              View Product
            </button>
          `}
        </div>
      `).join('')}
    </div>
  ` : ''}
</div>
            </div>
            <div class="dq-dosha-triad">
              <div class="dq-dosha-circle dq-vata">
                <img src="data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20320%20320%22%3E%0A%20%20%20%20%20%20%20%20%3Ccircle%20cx%3D%22160%22%20cy%3D%22160%22%20r%3D%22150%22%20fill%3D%22%23d6b34d%22%2F%3E%0A%20%20%20%20%20%20%20%20%3Cg%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-width%3D%224%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M160%2078c-18%2024%202%2036%2019%2045%2015%208%2022%2024%2016%2042-7%2019-26%2029-45%2025-19-4-31-20-31-39%200-24%2022-30%2033-46%208-11%207-20%208-27z%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M126%20145c-22%2010-34%2031-29%2052%207%2028%2034%2045%2064%2039%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M198%20148c20%2011%2031%2030%2027%2049-5%2025-26%2045-58%2047%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M140%20108c-7%2011-5%2024%206%2034%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%3C%2Fg%3E%0A%20%20%20%20%20%20%3C%2Fsvg%3E" alt="">
              </div>
              <div class="dq-dosha-circle dq-pitta">
                <img src="data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20320%20320%22%3E%0A%20%20%20%20%20%20%20%20%3Ccircle%20cx%3D%22160%22%20cy%3D%22160%22%20r%3D%22150%22%20fill%3D%22%23b75a33%22%2F%3E%0A%20%20%20%20%20%20%20%20%3Cg%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-width%3D%224%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M160%2074c-19%2025-39%2047-39%2080%200%2028%2018%2051%2039%2067%2021-16%2039-39%2039-67%200-33-20-55-39-80z%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M161%20117c-8%2018%208%2022%2016%2035%208%2012%207%2031-4%2042%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M138%20141c-10%2010-12%2024-6%2038%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%3C%2Fg%3E%0A%20%20%20%20%20%20%3C%2Fsvg%3E" alt="">
              </div>
              <div class="dq-dosha-circle dq-kapha">
                <img src="data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20320%20320%22%3E%0A%20%20%20%20%20%20%20%20%3Ccircle%20cx%3D%22160%22%20cy%3D%22160%22%20r%3D%22150%22%20fill%3D%22%23346f86%22%2F%3E%0A%20%20%20%20%20%20%20%20%3Cg%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-width%3D%224%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M208%20103c-27%200-50%2018-62%2041-10%2019-8%2038-1%2056%208%2020%2026%2036%2050%2036%2031%200%2058-25%2058-56%200-35-20-77-45-77z%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M158%20163c17-19%2049-25%2064-8%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M180%20128c12%202%2021%208%2028%2018%22%20%2F%3E%0A%20%20%20%20%20%20%20%20%3C%2Fg%3E%0A%20%20%20%20%20%20%3C%2Fsvg%3E" alt="">
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT -->
        <div class="dq-result-side">
          <div class="dq-result-card">

            <div class="dq-result-kicker">Your Imbalance is ${escapeHtml(vr.rightTitle || vr.leftTitle || formatTitle(vikritiKey))}</div>

              ${vr.intro ? `
    <p class="dq-result-paragraph">
      ${vr.intro}
    </p>
  ` : ''}

  ${vr.symptoms ? `
    <div class="dq-result-block">
      <h4>Common Signs</h4>
      <ul>
        ${vr.symptoms.map(item => `
          <li>${item}</li>
        `).join('')}
      </ul>
    </div>
  ` : ''}
${vr.bodyNeeds ? `
  <div class="dq-result-block">
    <h4>${vr.needsTitle || 'What Your Body Needs Right Now'}</h4>
    <ul>
      ${vr.bodyNeeds.map(item => `
        <li>${item}</li>
      `).join('')}
    </ul>
  </div>
` : ''}
          </div>
        </div>

      </div>
    </div>
  `;
  }

  function bindStepEvents(stepIndex) {
    const root = document.getElementById(appId);
    if (!root) return;

    const actionContinue = $('[data-action="continue"]', root);
    const actionBack = $('[data-action="back"]', root);

    if (actionContinue) actionContinue.addEventListener('click', handleContinue);
    if (actionBack) actionBack.addEventListener('click', handleBack);

    if (stepIndex === 0) {
      const form = $('#dq-email-form', root);
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = $('#dq-email-input', root);
        const val = (input?.value || '').trim();
        if (!val) return;
        state.email = val;
        nextStep();
      });
    }

    if (stepIndex >= 2 && stepIndex <= 5) {
      const questionIndex = stepIndex - 2;
      const optionRows = $all('.dq-option-row', root);

      optionRows.forEach((row) => {
        const optIndex = Number(row.dataset.optionIndex);
        const dosha = row.dataset.dosha;

        const rankBtns = $all('.dq-rank-btn', row);

        rankBtns.forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.classList.contains('disabled')) return;

            const rank = parseInt(btn.dataset.rank);

            setRank(questionIndex, optIndex, rank, dosha, root);
          });
        });
      });
    }

    if (stepIndex === 7) {
      const primary = $('#dq-primary', root);
      const secondary = $('#dq-secondary', root);
      const tertiary = $('#dq-tertiary', root);

      const preventDupes = () => {
        const vals = [primary.value, secondary.value, tertiary.value].filter(Boolean);
        const uniq = new Set(vals);
        if (uniq.size !== vals.length) {
          secondary.value = secondary.value === primary.value ? '' : secondary.value;
          tertiary.value = tertiary.value === primary.value || tertiary.value === secondary.value ? '' : tertiary.value;
        }
      };

      [primary, secondary, tertiary].forEach((el) => el?.addEventListener('change', preventDupes));
    }


    const infoIcons = $all('.dq-info-icon', root);

    infoIcons.forEach((icon) => {
      icon.addEventListener('mouseenter', () => {
        // remove existing
        document.querySelectorAll('.dq-tooltip').forEach(el => el.remove());

        const info = JSON.parse(icon.dataset.info);

        const tooltip = document.createElement('div');
        tooltip.className = 'dq-tooltip';

        tooltip.innerHTML = `
      <div class="dq-tooltip-content">
        ${info.map(item => `<div class="dq-tooltip-item">• ${item}</div>`).join('')}
      </div>
      <div class="dq-tooltip-arrow"></div>
    `;

        document.body.appendChild(tooltip);

        const rect = icon.getBoundingClientRect();

        tooltip.style.top = `${rect.bottom + window.scrollY + 10}px`;
        tooltip.style.left = `${rect.left + window.scrollX - 10}px`;

        icon._tooltip = tooltip;
      });

      icon.addEventListener('mouseleave', () => {
        if (icon._tooltip) {
          icon._tooltip.remove();
          icon._tooltip = null;
        }
      });
    });
  }

  function setRank(questionIndex, optionIndex, rank, dosha, root) {
    if (!state.rankSelections[questionIndex]) {
      state.rankSelections[questionIndex] = {};
    }

    const row = state.rankSelections[questionIndex];

    // ❌ remove this option if already selected (toggle behavior)
    if (row[optionIndex]?.rank === rank) {
      delete row[optionIndex];
    } else {
      // ✅ remove any option that already has this rank
      Object.keys(row).forEach((key) => {
        if (row[key].rank === rank) {
          delete row[key];
        }
      });

      // ✅ assign new rank
      row[optionIndex] = { rank, dosha };
    }

    syncRankHighlightsInQuestion(questionIndex, root);
  }

  function syncRankHighlights(currentStepIndex) {
    if (currentStepIndex < 2 || currentStepIndex > 5) return;
    syncRankHighlightsInQuestion(currentStepIndex - 2, document.getElementById(appId));
  }

  function syncRankHighlightsInQuestion(questionIndex, root) {
    const row = state.rankSelections[questionIndex] || {};

    // Get used ranks
    const usedRanks = Object.values(row).map(x => x.rank);

    const options = root.querySelectorAll('.dq-option-row');

    options.forEach((optionEl, optIdx) => {
      const rankBtns = optionEl.querySelectorAll('.dq-rank-btn');

      rankBtns.forEach(btn => {
        const rank = parseInt(btn.dataset.rank);

        const isSelected = row[optIdx]?.rank === rank;
        const isUsedElsewhere =
          usedRanks.includes(rank) && !isSelected;

        // RESET
        btn.classList.remove('active', 'disabled');

        if (isSelected) {
          btn.classList.add('active');
        } else if (isUsedElsewhere) {
          btn.classList.add('disabled');
        }
      });
    });
  }
  function getOppositeDosha(prakriti) {
    const parts = prakriti.split('-');

    const all = ['vata', 'pitta', 'kapha'];

    // remove constitution doshas
    const remaining = all.filter(d => !parts.includes(d));

    if (remaining.length === 1) {
      return remaining[0];
    }

    return remaining.join('-');
  }
  function formatQuizDataForEmail() {
    const scores = calculateScores();
    const prakriti = getDoshaResult(scores);
    const imbalance = getOppositeDosha(prakriti);

    const formattedAnswers = PRAKRITI_QUESTIONS.map((q, qIndex) => {
      const row = state.rankSelections[qIndex] || {};

      const sorted = Object.entries(row)
        .sort((a, b) => a[1].rank - b[1].rank)
        .map(([optIndex, val]) => {
          const opt = q.options[optIndex];
          return `${val.rank}. ${opt.text} (${val.dosha})`;
        });

      return `Q${qIndex + 1}: ${q.title}\n${sorted.join('\n')}`;
    });

    return {
      name: state.name,
      email: state.email,
      phone: state.phone,
      city: state.city,
      age: state.age,
      gender: state.gender,
      concerns: state.vikritiConcerns,
      answers: formattedAnswers.join('\n\n'),
      prakriti,
      imbalance
    };
  }
  function sendQuizEmail(data) {
    return fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        service_id: "service_8e3zayh",
        template_id: "template_rfu7ugk",
        user_id: "05KtKMXIkXcSQGQJe",
        template_params: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          city: data.city,
          age: data.age,
          gender: data.gender,
          prakriti: data.prakriti,
          imbalance: data.imbalance,
          concerns: JSON.stringify(data.concerns),
          answers: data.answers
        }
      })
    });
  }
  function handleContinue() {
    const current = getCurrentStep();

    if (current >= 2 && current <= 5) {
      const qIndex = current - 2;
      if (!isQuestionComplete(qIndex)) {
        alert('Please select 1, 2, and 3 once each for this question.');
        return;
      }
      nextStep();
      return;
    }

    if (current === 1 || current === 6) {
      nextStep();
      return;
    }

    if (current === 7) {
      const primary = $('#dq-primary')?.value || '';
      if (!primary) {
        alert('Primary Concern is required.');
        return;
      }

      state.vikritiConcerns.primary = primary;
      state.vikritiConcerns.secondary = $('#dq-secondary')?.value || '';
      state.vikritiConcerns.tertiary = $('#dq-tertiary')?.value || '';
      // ✅ Prepare data
      const data = formatQuizDataForEmail();

      // ✅ Send email (non-blocking)
      sendQuizEmail(data)
        .then(() => console.log("Email sent"))
        .catch(() => console.log("Email failed"));
      nextStep();
      return;
    }
  }

  function handleBack() {
    if (state.step === 0) return;
    state.step -= 1;
    render();
  }

  function nextStep() {
    state.step += 1;
    render();
  }

  function startOrContinueFromEmail() {
    nextStep();
  }

  function normalizeResultKey(key) {
    const k = (key || '').toLowerCase().trim();
    if (k === 'vata-pitta-kapha' || k === 'kapha-pitta-vata') return 'tridoshic';
    if (k === 'vata') return 'vata';
    if (k === 'pitta') return 'pitta';
    if (k === 'kapha') return 'kapha';
    if (k.includes('vata') && k.includes('pitta')) return 'vata-pitta';
    if (k.includes('pitta') && k.includes('kapha')) return 'pitta-kapha';
    if (k.includes('vata') && k.includes('kapha')) return 'vata-kapha';
    return 'tridoshic';
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function escapeAttr(str) {
    return escapeHtml(str).replaceAll('\n', ' ');
  }

  document.addEventListener('DOMContentLoaded', () => {
    render();

    const root = document.getElementById(appId);
    root?.addEventListener('click', (e) => {
      const heroButton = e.target.closest('.dq-btn-red');

      if (heroButton && state.step === 0) {
        const form = document.getElementById('dq-email-form');

        // 🔥 This triggers full HTML validation
        if (!form.checkValidity()) {
          form.reportValidity(); // shows browser errors
          return;
        }

        // Now safely collect values
        state.email = form.email.value;
        state.name = form.name.value;
        state.phone = form.phone.value;
        state.city = form.city.value;
        state.age = form.age_group.value;
        state.gender = form.gender.value;

        startOrContinueFromEmail();
      }
    });
  });

  window.doshaQuiz = {
    nextStep,
    handleBack,
    state,
  };
  document.addEventListener('click', (e) => {
    const infoBtn = e.target.closest('.dq-info-icon');
    if (!infoBtn) return;

    const info = JSON.parse(infoBtn.dataset.info);
  });



})();